import useSWR from "swr";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "src/auth/hooks";
import axios, { fetcher } from "src/utils/axios";
import uuidv4 from "src/utils/uuidv4";
import {
  isPlatformPending,
  isPlatformRelease,
  type PendingPlatformPolicy,
  type PlatformLegalLocale,
  type PlatformPolicyRelease,
  type ProviderLegalRegistration,
} from "src/types/platform-legal";

export function usePlatformLegalLocale(): PlatformLegalLocale {
  const { i18n } = useTranslation();
  return i18n.language?.split("-")[0] === "hu" ? "hu" : "en";
}
export async function getCurrentPlatformPolicy([url]: readonly unknown[]) {
  const data: unknown = await fetcher(String(url));
  if (
    !data ||
    typeof data !== "object" ||
    !("release" in data) ||
    (data.release !== null && !isPlatformRelease(data.release))
  )
    throw new Error("Invalid platform policy response");
  return data as { release: PlatformPolicyRelease | null };
}
export function useCurrentPlatformPolicy() {
  const locale = usePlatformLegalLocale();
  const result = useSWR(
    [`/platform/legal/current?locale=${locale}`],
    getCurrentPlatformPolicy,
    {
      keepPreviousData: false,
      shouldRetryOnError: false,
      revalidateOnFocus: true,
    },
  );
  useEffect(() => {
    const refresh = () => {
      void result.mutate().catch(() => undefined);
    };
    window.addEventListener("platform-policy-changed", refresh);
    return () => window.removeEventListener("platform-policy-changed", refresh);
  }, [result.mutate]);
  return result;
}
export async function getPendingPlatformPolicy([url]: readonly unknown[]) {
  const data: unknown = await fetcher(String(url));
  if (!isPlatformPending(data))
    throw new Error("Invalid pending platform policy response");
  return data;
}
export function usePendingPlatformPolicy() {
  const { user } = useAuthContext();
  const locale = usePlatformLegalLocale();
  const sessionKey = useMemo(() => `${user?.id}:${uuidv4()}`, [user?.id]);
  return useSWR<{ items: PendingPlatformPolicy[] }>(
    user?.id
      ? [`/me/platform/legal/pending?locale=${locale}`, user.id, sessionKey]
      : null,
    getPendingPlatformPolicy,
    {
      keepPreviousData: false,
      shouldRetryOnError: false,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      refreshInterval: 60000,
    },
  );
}
export async function acceptPlatformPolicy(
  policy: PlatformPolicyRelease,
  capacity: "self" | "guardian",
) {
  return (
    await axios.post("/me/platform/legal/accept", {
      release_id: policy.id,
      checksum: policy.checksum,
      locale: policy.locale,
      terms_accepted: true,
      privacy_acknowledged: true,
      capacity,
    })
  ).data;
}

export function useProviderPlatformPolicy(providerId: number) {
  const { user } = useAuthContext();
  const locale = usePlatformLegalLocale();
  return useSWR(
    providerId && user?.id
      ? [
          `/providers/${providerId}/platform-legal/pending?locale=${locale}`,
          user.id,
        ]
      : null,
    async ([url]) => {
      const data = await fetcher(url);
      if (
        !data ||
        typeof data.provider_type !== "string" ||
        typeof data.can_accept !== "boolean" ||
        (data.pending !== null && !isPlatformRelease(data.pending))
      )
        throw new Error("Invalid provider policy response");
      return data as {
        provider_type: string;
        can_accept: boolean;
        pending: PlatformPolicyRelease | null;
      };
    },
  );
}
export async function acceptProviderPlatformPolicy(
  providerId: number,
  payload: ProviderLegalRegistration,
) {
  return (
    await axios.post(`/providers/${providerId}/platform-legal/accept`, payload)
  ).data;
}
